import { useAppDispatch, useAppSelector } from "@/lib/store";
import { auth_routes } from "@/routes/auth";
import { useLocation, Navigate } from "react-router";
import { Loader2 } from "lucide-react";
import SuspendedPage from "@/app/status/suspended.page";
import PendingPage from "@/app/status/pending.page.tsx";
import AccountUnavailablePage from "@/app/status/account-unavailable.page.tsx";
import { loadScopeWithoutOrganization } from "@/lib/config.slice.ts";
import { CONTACT_SCOPE } from "@/lib/property-contact-scope";
import { organization_user_status } from "@/models/organization-user.model.ts";
import { useEffect } from "react";
import { realtime } from "@/lib/realtime.ts";
import { ChatService } from "@/services/chat.service.ts";
import { pageIsLeaving } from "@/lib/page-leaving.ts";

const FINISH_SIGNUP_PATH = "/signup/finish";

const SIGNUP_FINISHING_PATHS: readonly string[] = [FINISH_SIGNUP_PATH, "/signup/invite"];

export function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading, signupPending } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { loading: organizationLoading, organizations } = useAppSelector((state) => state.organization);
  const { membership, loading: memLoading } = useAppSelector((s) => s.membership);
  const { loading: configLoading, routeScope, accountUnavailable } = useAppSelector((s) => s.config);
  const location = useLocation();
  const userId = user?.id;
  const chatAllowed = organizations.length > 0 || (routeScope !== null && routeScope !== CONTACT_SCOPE);

  useEffect(() => {
    return () => {
      realtime.shutdown();
    };
  }, [userId]);

  // Initialize global realtime services once the user is authenticated
  useEffect(() => {
    if (userId && !loading && !organizationLoading && !memLoading && chatAllowed) {
      realtime.initializeGlobalChatFeed(userId);
      ChatService.listChats().catch((error) => {
        if (!pageIsLeaving()) console.error("Failed to load chat rooms:", error);
      });
    }

    if (!userId && !loading) {
      realtime.shutdown();
    }
  }, [userId, loading, organizationLoading, memLoading, chatAllowed]);

  const authRoute = auth_routes.find((route) => route.path === location.pathname && !route.special);
  const isAuthRoute = authRoute !== undefined;

  if (loading || (user && (organizationLoading || memLoading || configLoading))) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user && accountUnavailable) return <AccountUnavailablePage onRetry={() => dispatch(loadScopeWithoutOrganization(user.id))} />;
  if (!user && signupPending && !SIGNUP_FINISHING_PATHS.includes(location.pathname)) return <Navigate to={FINISH_SIGNUP_PATH} />;
  if (!user && !isAuthRoute) return <Navigate to="/login" />;
  if (user && isAuthRoute && !authRoute.signedInToo) return <Navigate to="/" />;

  if (membership?.status === organization_user_status.suspended) return <SuspendedPage />;
  if (membership?.status === organization_user_status.pending) return <PendingPage />;

  return <>{children}</>;
}
